'use client';
/**
 * LE REGISTRE DES POINTS DE RÈGLE (lot REG-3, maquette 8).
 *
 * ── À quoi il sert, et pourquoi il ne suffit pas d'un « i » par écran ───────
 *
 * Les encarts sont dispersés : un par champ, là où le choix se fait. C'est leur
 * qualité. Mais le responsable qualité a deux questions qu'aucun encart ne peut
 * répondre depuis sa place :
 *
 *  · « Quelles règles ai-je approuvées, et dans quelle version ? » — la question
 *    qu'un auditeur pose, et à laquelle il faut répondre sans parcourir douze
 *    écrans un par un ;
 *  · « Lesquelles ne décrivent plus ce que le logiciel applique ? » — la
 *    question que l'alarme « à revalider » pose, et qu'il faut voir d'un coup
 *    d'œil, faute de quoi elle dort sur un écran que personne n'ouvre.
 *
 * ── Ce que cet écran ne fait PAS encore ─────────────────────────────────────
 *
 * Il ne MODIFIE rien. L'édition de l'énoncé en deux zones et le circuit
 * brouillon → approbation → date d'application demandent que les règles vivent
 * en base, alors qu'elles vivent aujourd'hui dans le code (`src/regles-nc.ts`).
 * C'est le lot suivant.
 *
 * Le dire ICI, à l'écran, plutôt que de laisser chercher un bouton absent :
 * un registre en lecture seule qui s'annonce comme tel est honnête ; le même
 * sans un mot passe pour cassé.
 */
import React from 'react';
import { useGed, userName } from '@/lib/store';
import type { RegleServie } from '@/lib/store';
import { I, card } from '@/components/ui';
import { EncartRegle } from '@/components/PointDeRegle';
import { fmtDate } from '@/lib/meta';

import { Bouton } from '@/shared/Bouton';
import { Tableau } from '@/shared/Tableau';

const pastille = (fond: string, encre: string, texte: string) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 'var(--radius-pill)',
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: fond, color: encre,
  }}>{texte}</span>
);

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5,
};

export function ReglesParametrage() {
  const { regles, settings } = useGed();
  const [ouverte, setOuverte] = React.useState<string | null>(null);

  const liste = (regles || []) as RegleServie[];
  /* Les règles réveillées EN TÊTE : ce sont les seules qui appellent un geste.
     Un tri par référence les noierait au milieu de celles qui vont bien, et
     l'alarme ne servirait qu'à ceux qui la cherchaient déjà. */
  const aRevalider = liste.filter((r) => r.etat === 'a_revalider');
  const calmes = liste.filter((r) => r.etat !== 'a_revalider');

  if (!liste.length) {
    return (
      <div style={{ ...card, padding: 18 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Aucun point de règle n’est servi par le moteur. Si l’application vient de démarrer,
          rechargez l’écran ; sinon, signalez-le à l’informatique — les « i » des écrans ne
          s’afficheront pas non plus.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ ...card, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>
            Règles affichées aux écrans
          </h3>
          {pastille('var(--cyan-50)', 'var(--cyan-800)', `${liste.length} règle(s)`)}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Ce que le laboratoire a décidé, affiché derrière le « i » à l’endroit exact où le choix se
          fait. Chaque règle porte sa référence, sa version, son approbateur et sa date d’application
          — c’est ce qui la rend opposable plutôt qu’indicative.
        </p>
      </div>

      {!!aRevalider.length && (
        <div style={{
          background: 'var(--amber-50)', border: '1px solid var(--amber-300)',
          borderRadius: 'var(--radius-md)', padding: '12px 15px',
        }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--text-strong)' }}>
            <I n="alert" s={14} />
            {aRevalider.length} règle(s) à revalider
          </strong>
          <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.55 }}>
            Le logiciel a changé depuis que ces énoncés ont été approuvés : les valeurs affichées
            sont à jour, l’explication qui les accompagne peut-être pas. Les encarts continuent de
            s’afficher aux écrans, <strong>avec cette mention</strong> — les masquer priverait de la
            règle ceux qui en ont besoin, et la taire laisserait lire comme opposable un énoncé
            que le logiciel n’applique plus.
          </p>
        </div>
      )}

      {/* L7 vague 2 (11/10/2026) : registre SIMPLE sur le socle. Les règles à
          revalider restent EN TÊTE par défaut (l'ordre métier, tant que personne
          ne trie) ; l'encart RÉEL — celui que le personnel voit derrière le « i »,
          pas une reconstitution — se déplie sous la ligne ; « Voir l'encart » est
          en colonne d'actions. */}
      <Tableau
        id="regles-parametrage"
        ajusterLargeur
        niveau="simple"
        lignes={[...aRevalider, ...calmes]}
        cleLigne={(r) => r.ref}
        ligneProps={(r) => ({ style: { background: r.etat === 'a_revalider' ? 'var(--amber-50)' : undefined } })}
        colonnes={[
          { cle: 'ref', libelle: 'Réf.', valeur: (r) => r.ref, style: { whiteSpace: 'nowrap' }, rendu: (r) => <span style={{ fontWeight: 700, color: 'var(--cyan-700)' }}>{r.ref}</span> },
          { cle: 'titre', libelle: 'Ce qu’elle explique', valeur: (r) => r.titre, rendu: (r) => r.titre },
          {
            cle: 'ancre', libelle: 'Où elle s’affiche', valeur: (r) => r.ancre, style: { whiteSpace: 'nowrap', color: 'var(--text-muted)' },
            /* L'ANCRE et non un libellé d'écran : c'est l'identifiant technique
               du point où la règle s'affiche, et c'est lui qu'on cherche quand on
               se demande « pourquoi ce champ montre-t-il ceci ? ». */
            rendu: (r) => <code style={{ fontSize: 12 }}>{r.ancre}</code>,
          },
          { cle: 'version', libelle: 'Version', type: 'nombre', valeur: (r) => r.version, rendu: (r) => `v${r.version}` },
          {
            cle: 'approuvee', libelle: 'Approuvée', type: 'date', valeur: (r) => r.depuis.applicableLe || r.depuis.le,
            style: { whiteSpace: 'nowrap', color: 'var(--text-muted)' },
            rendu: (r) => `${userName(settings, r.depuis.par)} · ${fmtDate(r.depuis.applicableLe || r.depuis.le)}`,
          },
          {
            cle: 'etat', libelle: 'État', type: 'liste', valeur: (r) => r.etat,
            libelleValeur: (v) => (v === 'a_revalider' ? 'à revalider' : 'applicable'),
            rendu: (r) => (r.etat === 'a_revalider'
              ? pastille('var(--amber-50)', 'var(--amber-700)', '⚠ à revalider')
              : pastille('var(--green-50)', 'var(--green-700)', 'applicable')),
          },
        ]}
        actions={(r) => (
          <Bouton taille="sm" onClick={() => setOuverte(ouverte === r.ref ? null : r.ref)} aria-expanded={ouverte === r.ref}>
            {ouverte === r.ref ? 'Replier' : 'Voir l’encart'}
          </Bouton>
        )}
        detail={(r) => (ouverte === r.ref ? <EncartRegle r={r} /> : null)}
      />

      <div style={{ ...card, padding: '13px 16px' }}>
        <span style={lbl}>Ce que cet écran ne fait pas encore</span>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Les règles se <strong>lisent</strong> ici, elles ne s’y modifient pas encore. L’édition de
          l’énoncé et le circuit <em>brouillon → approbation → date d’application</em> demandent que
          les règles vivent en base ; elles vivent aujourd’hui dans le code du moteur, ce qui est
          précisément ce qui garantit que les <strong>valeurs</strong> affichées sont celles qui
          s’appliquent. Les deux se concilient en séparant l’énoncé (au RQ) des valeurs (au
          logiciel) — c’est le lot suivant.
        </p>
      </div>
    </div>
  );
}
